import { Box, Skeleton, Stack, StackProps, Tooltip, Typography } from '@mui/material';
import { AssessmentMethodType, FilterKeys } from '@schooly/api';
import {
  ExpandedSelectFilter,
  FilterDropdown,
  FilterDropdownSingleSelect,
} from '@schooly/components/filters';
import { TagSelect } from '@schooly/style';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { COLOR_SCALE } from '../common/colouredCells';
import { MarkbookSettingsSwitch } from './MarkbookSettingsSwitch';

export type MarkbookColumnArrangeBy = FilterKeys.Subject | FilterKeys.Assessment;
export type MarkbookMethodType =
  | AssessmentMethodType.Score
  | AssessmentMethodType.Grade
  | AssessmentMethodType.Comment;

const MARKBOOK_SETTINGS_HEIGHT = 38;
const MARKBOOK_ARRANGE_BY_OPTIONS = [FilterKeys.Subject, FilterKeys.Assessment] as const;
const MARKBOOK_METHOD_TYPE_OPTIONS: MarkbookMethodType[] = [
  AssessmentMethodType.Score,
  AssessmentMethodType.Grade,
  AssessmentMethodType.Comment,
];

export const DEFAULT_MARKBOOK_VIEW_SETTINGS: MarkbookViewSettings = {
  columnArrangeBy: FilterKeys.Subject,
  methodType: [],
  avgResults: false,
  coloredCells: false,
  scoreInPercentage: false,
  editable: false,
};

export type MarkbookViewSettings = {
  columnArrangeBy: MarkbookColumnArrangeBy;
  methodType: MarkbookMethodType[];
  avgResults: boolean;
  coloredCells: boolean;
  scoreInPercentage: boolean;
  editable: boolean;
};
export type MarkbookSettingKey = Exclude<keyof MarkbookViewSettings, 'arrangeBy' | 'methodType'>;

export type MarkbookOverrideValueAndDisable = Partial<Record<MarkbookSettingKey, boolean>>;

export type MarkbookSettingsProps = {
  baselineEnabled: boolean;
  settings: MarkbookViewSettings;
  onSetSettings: Dispatch<SetStateAction<MarkbookViewSettings>>;
  overrideValueAndDisable: MarkbookOverrideValueAndDisable;
};

export const MarkbookSettings: FC<MarkbookSettingsProps> = ({
  baselineEnabled,
  settings,
  onSetSettings,
  overrideValueAndDisable,
}) => {
  const { formatMessage } = useIntl();

  const {
    updateSettings,
    updateArrangeBy,
    setMethodType,
    clearMethodType,
    getArrangeByOptionLabelId,
    getMethodTypeOptionLabelId,
  } = useMemo(() => {
    return {
      updateSettings: (key: MarkbookSettingKey, value: boolean) => {
        onSetSettings((prev) => ({ ...prev, [key]: value }));
      },
      updateArrangeBy: (value: MarkbookColumnArrangeBy) => {
        onSetSettings((prev) => ({ ...prev, columnArrangeBy: value }));
      },
      setMethodType: (value: MarkbookMethodType[]) => {
        onSetSettings((prev) => ({
          ...prev,
          methodType: value,
        }));
      },
      clearMethodType: () => {
        onSetSettings((prev) => ({
          ...prev,
          methodType: [],
        }));
      },
      getArrangeByOptionLabelId: (option: MarkbookColumnArrangeBy) => {
        switch (option) {
          case FilterKeys.Subject:
            return 'assessments-markbook-subject';
          case FilterKeys.Assessment:
            return 'assessments-markbook-assessment';
          default:
            return '';
        }
      },
      getMethodTypeOptionLabelId: (option: MarkbookMethodType) => {
        switch (option) {
          case AssessmentMethodType.Score:
          case AssessmentMethodType.Grade:
          case AssessmentMethodType.Comment:
            return `assessments-Type-${option}`;
          default:
            return '';
        }
      },
    };
  }, [onSetSettings]);

  const arrangeByLabel = formatMessage({ id: 'filter-ArrangeBy' });
  const methodTypeLabel = formatMessage({ id: 'assessments-Type' });
  const avgResultsLabel = formatMessage({ id: 'assessments-markbook-AvgResults' });
  const coloredCellsLabel = formatMessage({ id: 'assessments-markbook-ColoredCells' });
  const scoreInPercentageLabel = formatMessage({ id: 'assessments-markbook-ScoreInPercentage' });
  const unlockEditingLabel = formatMessage({ id: 'assessments-markbook-UnlockEditing' });
  const isAvgResultsDisabled = overrideValueAndDisable.avgResults !== undefined;
  const isColoredCellsDisabled = overrideValueAndDisable.coloredCells !== undefined;
  const isScoreInPercentageDisabled = overrideValueAndDisable.scoreInPercentage !== undefined;
  const isUnlockEditingDisabled = overrideValueAndDisable.editable !== undefined;

  return (
    <Stack
      direction="row"
      alignItems="center"
      minHeight={MARKBOOK_SETTINGS_HEIGHT}
      gap={2}
      p={0.5}
      flexWrap="wrap"
    >
      <FilterDropdownSingleSelect
        onSelectOption={updateArrangeBy}
        options={MARKBOOK_ARRANGE_BY_OPTIONS}
        selectedOption={settings.columnArrangeBy}
        label={arrangeByLabel}
        getOptionLabelId={getArrangeByOptionLabelId}
      />

      <FilterDropdown
        label={methodTypeLabel}
        onClear={clearMethodType}
        tags={(open) =>
          settings.methodType.map((methodType) => (
            <TagSelect
              key={methodType}
              sx={{ maxWidth: 200 }}
              onClick={open}
              label={formatMessage({ id: getMethodTypeOptionLabelId(methodType) })}
            />
          ))
        }
      >
        {(onClose) => (
          <ExpandedSelectFilter
            selected={settings.methodType}
            onApply={setMethodType}
            onClose={onClose}
            options={MARKBOOK_METHOD_TYPE_OPTIONS}
            getLabel={(i) => formatMessage({ id: getMethodTypeOptionLabelId(i) })}
            onClear={() => {
              clearMethodType();
              onClose();
            }}
          />
        )}
      </FilterDropdown>

      <Divider />
      <MarkbookSettingsSwitch
        disabled={isAvgResultsDisabled}
        label={avgResultsLabel}
        value={isAvgResultsDisabled ? !!overrideValueAndDisable.avgResults : settings.avgResults}
        onChange={(value) => {
          updateSettings('avgResults', value);
          if (value && !isColoredCellsDisabled) {
            updateSettings('coloredCells', true);
          }
        }}
      />
      <Tooltip
        slotProps={{ tooltip: { sx: { p: 1.5 } } }}
        title={<ColoredCellsTooltip baselineEnabled={baselineEnabled} />}
      >
        <div>
          <MarkbookSettingsSwitch
            disabled={isColoredCellsDisabled}
            label={coloredCellsLabel}
            value={
              isColoredCellsDisabled
                ? !!overrideValueAndDisable.coloredCells
                : settings.coloredCells
            }
            onChange={(value) => updateSettings('coloredCells', value)}
          />
        </div>
      </Tooltip>
      <MarkbookSettingsSwitch
        disabled={isScoreInPercentageDisabled}
        label={scoreInPercentageLabel}
        value={
          isScoreInPercentageDisabled
            ? !!overrideValueAndDisable.scoreInPercentage
            : settings.scoreInPercentage
        }
        onChange={(value) => updateSettings('scoreInPercentage', value)}
      />
      <Divider />
      <MarkbookSettingsSwitch
        disabled={isUnlockEditingDisabled}
        label={unlockEditingLabel}
        value={isUnlockEditingDisabled ? !!overrideValueAndDisable.editable : settings.editable}
        onChange={(value) => updateSettings('editable', value)}
      />
    </Stack>
  );
};

export const MarkbookSettingsLoading: FC = () => (
  <Stack
    direction="row"
    alignItems="center"
    minHeight={MARKBOOK_SETTINGS_HEIGHT}
    gap={2}
    p={0.5}
    flexWrap="wrap"
  >
    <Skeleton variant="rectangular" width={140} height={22} />
    <Skeleton variant="rectangular" width={140} height={22} />
    <Divider />
    <Skeleton variant="rectangular" width={150} height={22} />
    <Skeleton variant="rectangular" width={110} height={22} />
    <Skeleton variant="rectangular" width={90} height={22} />
    <Divider />
    <Skeleton variant="rectangular" width={90} height={22} />
  </Stack>
);

const Divider: FC<StackProps> = ({ ...stackProps }) => (
  <Stack
    sx={(theme) => ({
      borderRight: `1px solid ${theme.palette.divider}`,
      height: theme.spacing(2.5),
      mx: theme.spacing(1),
    })}
    {...stackProps}
  />
);

const ColoredCellsTooltip: FC<{ baselineEnabled: boolean }> = ({ baselineEnabled }) => {
  const { formatMessage } = useIntl();
  return (
    <Stack gap={1.5} alignItems="flex-start">
      <Typography>
        {baselineEnabled
          ? formatMessage({ id: 'assessments-markbook-ColoredCellsTooltip-Baseline' })
          : formatMessage({ id: 'assessments-markbook-ColoredCellsTooltip-Average' })}
      </Typography>
      <Stack gap={0.5}>
        <ColorLabel
          color={COLOR_SCALE.maxPositive}
          label={formatMessage({ id: 'assessments-markbook-ColoredCellsTooltip-Higher' })}
        />
        <ColorLabel
          color={COLOR_SCALE.neutral}
          label={formatMessage({ id: 'assessments-markbook-ColoredCellsTooltip-Neutral' })}
        />
        <ColorLabel
          color={COLOR_SCALE.maxNegative}
          label={formatMessage({ id: 'assessments-markbook-ColoredCellsTooltip-Lower' })}
        />
      </Stack>
    </Stack>
  );
};

const ColorLabel = ({ color, label }: { color: string; label: string }) => (
  <Stack direction="row" gap={1} alignItems="center">
    <Box
      component="span"
      sx={(theme) => ({
        width: theme.spacing(2.15),
        borderRadius: '100%',
        height: theme.spacing(2.15),
        backgroundColor: color,
      })}
    />
    <Typography>{label}</Typography>
  </Stack>
);
